import { LegacySearchIndexCourse } from '@shared/types';
import { WebCourseSearchFilters } from '../../../services/search/course/useCourseSearch';
import {
    Graph,
    ItemList,
    ListItem,
    Place,
    SearchResultsPage,
    Specialty,
    Course as CourseSchema,
} from 'schema-dts';
import {
    LocationFilter,
    convertRadiusToMeters,
    knownLocationToGeoCoordinates,
} from '../../../services/search/locationFilter';
import { useCategories } from '../../../hooks/useCategories';
import { useMemo } from 'react';

const getFullCourseURL = (course: LegacySearchIndexCourse): string => {
    const partial = `/${course.educator_slug}/${course.slug}`;
    return `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}${partial}`;
};

const getCourseResultsItemListSchema = (courses: Array<LegacySearchIndexCourse>): ItemList => {
    return {
        '@type': 'ItemList',
        itemListElement: courses.map((course, index): ListItem => {
            return {
                '@type': 'ListItem',
                position: index + 1,
                url: getFullCourseURL(course),
            };
        }),
    };
};

const getSpecialtyFromCategory = (categoryName: string): { specialty: Specialty } | {} => {
    return {
        specialty: {
            '@type': 'Specialty',
            name: categoryName,
        },
    };
};

const getSpatialCoverageFromLocation = (
    location: LocationFilter
): { spatialCoverage: Place } | {} => {
    if ('known_location' in location) {
        const geo = knownLocationToGeoCoordinates(location.known_location);
        return {
            spatialCoverage: {
                '@type': 'City',
                name: location.known_location,
                address: location.known_location,
                geo: {
                    '@type': 'GeoCircle',
                    geoMidpoint: {
                        '@type': 'GeoCoordinates',
                        latitude: geo.latitude,
                        longitude: geo.longitude,
                    },
                    geoRadius: convertRadiusToMeters(location.radius),
                },
            },
        };
    }
    return {};
};

const getSearchResultPageSchema = (
    location?: LocationFilter,
    categoryName?: string
): SearchResultsPage => {
    return {
        '@type': 'SearchResultsPage',
        ...(categoryName ? getSpecialtyFromCategory(categoryName) : {}),
        ...(location ? getSpatialCoverageFromLocation(location) : {}),
    };
};

export const useCoursesSchema = (
    courses: Array<LegacySearchIndexCourse & { schema?: CourseSchema }>,
    filters: WebCourseSearchFilters
): Graph => {
    const { data: categories } = useCategories(
        {
            category_refs: [],
            category_slugs: filters.category_slug || [],
        },
        {
            keepPreviousData: true,
        }
    );

    const categoryName = useMemo(() => {
        if (!categories) {
            return '';
        }
        if (!categories.length) {
            return '';
        }
        const category = categories[0];
        if (category.page_title) {
            return category.page_title;
        }
        return category.name;
    }, [categories]);

    const itemListSchema = useMemo(() => {
        return getCourseResultsItemListSchema(courses);
    }, [courses]);

    const searchResultSchema = useMemo(() => {
        return getSearchResultPageSchema(filters.location, categoryName);
    }, [filters, categoryName]);

    const courseSchemas = useMemo(() => {
        return courses
            .map((course) => {
                return course.schema;
            })
            .filter((schema): schema is CourseSchema => schema !== undefined);
    }, [courses]);

    return {
        '@context': 'https://schema.org',
        '@graph': [itemListSchema, searchResultSchema, ...courseSchemas],
    };
};

export default useCoursesSchema;
